import "ag-grid-enterprise";
import React, { useMemo, useState } from "react";
import {
  Badge,
  Box,
  Group,
  Pagination,
  SegmentedControl,
  Select,
  Stack,
  Text,
} from "@mantine/core";
import { AgGridReact } from "ag-grid-react";
import type {
  ColDef,
  ColGroupDef,
  ICellRendererParams,
  ValueFormatterParams,
  ValueGetterParams,
} from "ag-grid-enterprise";
import {
  DROFUS_INTERFACE_OCCURRENCE_FIELD_GROUPS,
  DROFUS_INTERFACE_OCCURRENCE_FIELD_META,
  DROFUS_INTERFACE_OCCURRENCE_SELECT_FIELDS,
} from "@multiconsult-group/multiplacer-core";
import { useSelector } from "react-redux";
import { selectAgGridStateByKey } from "../agGridStateSlice";
import { useAgGridReduxState } from "../hooks/useAgGridReduxState";
import { useProjectId } from "../hooks/useProjectId";
import { openapi, type PostMultiplacerDrofusInterfaceOccurrencesApiResponse } from "../../openapi";
import { skipToken } from "@reduxjs/toolkit/query";
import { useAppDispatch, useAppSelector } from "../app/hooks";
import {
  selectDrofusForekomsterOccurrencesApiArg,
  selectDrofusForekomsterTableViewMode,
  setDrofusForekomsterLimit,
  setDrofusForekomsterOffset,
  setDrofusForekomsterTableViewMode,
} from "./drofusForekomsterSlice";
import { DrofusForekomstDetailsModal } from "./DrofusForekomstDetailsModal";

type DrofusForekomstRow = PostMultiplacerDrofusInterfaceOccurrencesApiResponse["items"][number];

const groupLabelById: Record<string, string> = {
  occurrence: "Forekomst",
  room: "Rom",
  article: "Artikkel",
  system: "System",
  tfmStatus: "TFM/status",
  occurrenceData: "Forekomstdata",
  articleNeeds: "Artikkelbehov",
};

const groupColorById: Record<string, string> = {
  occurrence: "gray",
  room: "blue",
  article: "cyan",
  system: "indigo",
  tfmStatus: "violet",
  occurrenceData: "grape",
  articleNeeds: "teal",
};

const disciplineColorById: Record<string, string> = {
  RIE: "#f59f00",
  RIKT: "#1971c2",
  RIAut: "#9c36b5",
  RIV: "#0c8599",
};

const defaultColDef: ColDef<DrofusForekomstRow> = {
  sortable: false,
  filter: false,
  resizable: true,
  enableRowGroup: false,
  enablePivot: false,
  enableValue: false,
  minWidth: 130,
};

const columnsOnlySideBar = {
  toolPanels: [
    {
      id: "columns",
      labelDefault: "Kolonner",
      labelKey: "columns",
      iconKey: "columns",
      toolPanel: "agColumnsToolPanel",
      toolPanelParams: {
        suppressRowGroups: true,
        suppressValues: true,
        suppressPivots: true,
        suppressPivotMode: true,
      },
    },
  ],
};

function getByPath(source: unknown, path: string): unknown {
  return path.split(".").reduce<unknown>((current, key) => {
    if (current == null || typeof current !== "object") return undefined;
    return (current as Record<string, unknown>)[key];
  }, source);
}

function formatGridValue(value: unknown): string {
  if (value == null || value === "") return "";
  if (typeof value === "boolean") return value ? "Ja" : "Nei";
  if (Array.isArray(value)) return value.join(", ");
  if (value instanceof Date) return value.toISOString();
  if (typeof value === "object") return JSON.stringify(value);
  return String(value);
}

const BooleanBadgeRenderer: React.FC<ICellRendererParams<DrofusForekomstRow, unknown>> = ({
  value,
}) => {
  if (typeof value !== "boolean") return <>{formatGridValue(value)}</>;

  return (
    <Badge
      size="sm"
      radius="sm"
      styles={{
        root: {
          backgroundColor: value ? "#d3f9d8" : "#f1f3f5",
          border: `1px solid ${value ? "#2f9e44" : "#ced4da"}`,
          color: value ? "#2b8a3e" : "#495057",
        },
        label: {
          color: "inherit",
        },
      }}
    >
      {value ? "Ja" : "Nei"}
    </Badge>
  );
};

const DisciplineBadgeRenderer: React.FC<ICellRendererParams<DrofusForekomstRow, string[]>> = ({
  value,
}) => {
  if (!Array.isArray(value) || value.length === 0) return null;

  return (
    <Group gap={4} wrap="nowrap">
      {value.map((discipline) => (
        <Badge
          key={discipline}
          size="sm"
          radius="sm"
          styles={{
            root: {
              backgroundColor: "#ffffff",
              border: `1px solid ${disciplineColorById[discipline] ?? "#868e96"}`,
              color: disciplineColorById[discipline] ?? "#495057",
            },
            label: {
              color: "inherit",
            },
          }}
        >
          {discipline}
        </Badge>
      ))}
    </Group>
  );
};

const ForekomstIdRenderer: React.FC<
  ICellRendererParams<DrofusForekomstRow, number> & {
    onOpenDetails?: (row: DrofusForekomstRow) => void;
  }
> = ({ value, data, onOpenDetails }) => {
  if (!data) return <>{value}</>;

  return (
    <button
      type="button"
      onClick={() => onOpenDetails?.(data)}
      style={{
        background: "transparent",
        border: 0,
        color: "#1971c2",
        cursor: "pointer",
        font: "inherit",
        fontWeight: 700,
        padding: 0,
        textDecoration: "underline",
      }}
    >
      {value}
    </button>
  );
};

export const DrofusForekomsterTable: React.FC = () => {
  const projectNo = useProjectId();
  const dispatch = useAppDispatch();
  const occurrencesApiArg = useAppSelector(selectDrofusForekomsterOccurrencesApiArg);
  const tableViewMode = useAppSelector(selectDrofusForekomsterTableViewMode);
  const gridKey = `drofusForekomsterTableNoLocalOps:${projectNo}`;
  const saved = useSelector(selectAgGridStateByKey(gridKey));
  const hasSavedCols = !!(saved?.columnState && saved.columnState.length > 0);
  const [detailsRow, setDetailsRow] = useState<DrofusForekomstRow | null>(null);

  const {
    data: occurrencesResponse,
    isLoading,
    isFetching,
    isError,
  } = openapi.endpoints.postMultiplacerDrofusInterfaceOccurrences.useQuery(
    occurrencesApiArg ?? skipToken
  );

  const rowData = occurrencesResponse?.items ?? [];
  const totalRows = occurrencesResponse?.total ?? 0;
  const limit = occurrencesResponse?.limit ?? occurrencesApiArg?.body.limit ?? 100;
  const offset = occurrencesResponse?.offset ?? occurrencesApiArg?.body.offset ?? 0;
  const totalPages = Math.max(1, Math.ceil(totalRows / limit));
  const activePage = Math.floor(offset / limit) + 1;
  const firstVisibleRow = totalRows === 0 ? 0 : offset + 1;
  const lastVisibleRow = Math.min(offset + rowData.length, totalRows);

  const allColIds = useMemo(
    () => [
      "occurrenceId",
      "interfaceRelevant",
      "affectedDisciplines",
      "simpleRoomFunctionNo",
      "simpleRoomName",
      "simpleArticleCompCode",
      "simpleArticleName",
      "simpleToBeDrawn",
      "simpleQuantity",
      ...DROFUS_INTERFACE_OCCURRENCE_SELECT_FIELDS,
    ],
    []
  );
  const columnsSignature = useMemo(
    () => `noLocalOps:v2:${tableViewMode}:${allColIds.join("|")}`,
    [allColIds, tableViewMode]
  );

  const { gridStateHandlers } = useAgGridReduxState(gridKey, {
    availableColIds: allColIds,
    columnsSignature,
    managePivotMode: false,
  });

  const columnDefs = useMemo<Array<ColDef<DrofusForekomstRow> | ColGroupDef<DrofusForekomstRow>>>(
    () => {
      const fixedColumns: ColDef<DrofusForekomstRow>[] = [
        {
          headerName: "Forekomst ID",
          field: "occurrenceId",
          colId: "occurrenceId",
          pinned: "left",
          width: 130,
          filter: false,
          cellRenderer: "ForekomstIdRenderer",
          cellRendererParams: {
            onOpenDetails: setDetailsRow,
          },
        },
        {
          headerName: "Interface-relevant",
          field: "interfaceRelevant",
          colId: "interfaceRelevant",
          pinned: "left",
          width: 160,
          cellRenderer: "BooleanBadgeRenderer",
        },
        {
          headerName: "Fag",
          field: "affectedDisciplines",
          colId: "affectedDisciplines",
          pinned: "left",
          width: 160,
          cellRenderer: "DisciplineBadgeRenderer",
          valueFormatter: ({ value }: ValueFormatterParams<DrofusForekomstRow, string[]>) =>
            Array.isArray(value) ? value.join(", ") : "",
        },
      ];

      if (tableViewMode === "simple") {
        return [
          ...fixedColumns,
          {
            headerName: "Romsnummer",
            colId: "simpleRoomFunctionNo",
            width: 150,
            valueGetter: ({ data }) => data?.normalized.room.functionNo,
          },
          {
            headerName: "Romsnavn",
            colId: "simpleRoomName",
            flex: 1,
            minWidth: 220,
            valueGetter: ({ data }) => data?.normalized.room.fullName,
          },
          {
            headerName: "CompCode",
            colId: "simpleArticleCompCode",
            width: 150,
            valueGetter: ({ data }) => data?.normalized.article.compCode,
          },
          {
            headerName: "Artikkelnavn",
            colId: "simpleArticleName",
            flex: 1,
            minWidth: 260,
            valueGetter: ({ data }) => data?.normalized.article.name,
          },
          {
            headerName: "Skal modelleres",
            colId: "simpleToBeDrawn",
            width: 150,
            cellRenderer: "BooleanBadgeRenderer",
            valueGetter: ({ data }) => data?.normalized.article.toBeDrawn,
          },
          {
            headerName: "Antall",
            colId: "simpleQuantity",
            width: 110,
            valueGetter: ({ data }) => data?.normalized.occurrence.quantity,
          },
        ];
      }

      const metadataGroups = DROFUS_INTERFACE_OCCURRENCE_FIELD_GROUPS.map((group) => {
        const fields = DROFUS_INTERFACE_OCCURRENCE_FIELD_META.filter((field) => field.group === group);

        return {
          headerName: groupLabelById[group] ?? group,
          marryChildren: true,
          headerClass: `drofus-forekomster-group-${group}`,
          children: fields.map((field): ColDef<DrofusForekomstRow> => {
            const isBooleanLike =
              field.normalizedPath?.startsWith("needs.") ||
              field.normalizedPath?.startsWith("system.is") ||
              field.normalizedPath === "article.toBeDrawn" ||
              field.normalizedPath === "article.bip" ||
              field.normalizedPath === "occurrence.isSubOccurrence" ||
              field.normalizedPath === "occurrence.hasIfcGuids";

            return {
              headerName: field.displayName,
              headerTooltip: `${field.id}${
                field.normalizedPath ? ` -> normalized.${field.normalizedPath}` : ""
              }${field.affectedDiscipline ? ` -> ${field.affectedDiscipline}` : ""}`,
              colId: field.id,
              width: field.group === "articleNeeds" ? 260 : 210,
              filter: false,
              cellRenderer: isBooleanLike ? "BooleanBadgeRenderer" : undefined,
              valueGetter: ({ data }: ValueGetterParams<DrofusForekomstRow>) =>
                field.normalizedPath && data
                  ? getByPath(data.normalized, field.normalizedPath)
                  : undefined,
              valueFormatter: ({ value }: ValueFormatterParams<DrofusForekomstRow>) =>
                formatGridValue(value),
            };
          }),
        } satisfies ColGroupDef<DrofusForekomstRow>;
      });

      return [...fixedColumns, ...metadataGroups];
    },
    [tableViewMode]
  );

  return (
    <Stack h="100%" w="100%" gap="sm">
      <DrofusForekomstDetailsModal
        row={detailsRow}
        opened={detailsRow != null}
        onClose={() => setDetailsRow(null)}
      />

      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap="sm" wrap="nowrap">
          <Group gap={6} wrap="nowrap">
            <Text size="xs" fw={700} c="dimmed" style={{ whiteSpace: "nowrap" }}>
              VISNING
            </Text>
            <SegmentedControl
              size="xs"
              color="blue"
              value={tableViewMode}
              onChange={(value) =>
                dispatch(setDrofusForekomsterTableViewMode(value as "simple" | "full"))
              }
              data={[
                { value: "simple", label: "Normalvisning" },
                { value: "full", label: "Alle mappingfelt" },
              ]}
              styles={{
                root: {
                  border: "1px solid var(--mantine-color-blue-3)",
                  backgroundColor: "var(--mantine-color-blue-0)",
                },
                label: {
                  fontWeight: 700,
                },
              }}
            />
          </Group>
          <Text size="sm" c="dimmed" style={{ whiteSpace: "nowrap" }}>
            {isError
              ? "Kunne ikke laste forekomster"
              : occurrencesApiArg
                ? `${firstVisibleRow}-${lastVisibleRow} av ${totalRows}`
                : "Velg en kjøring"}
          </Text>
          {isFetching && (
            <Badge variant="light" color="blue" radius="sm">
              Laster
            </Badge>
          )}
        </Group>
        {tableViewMode === "full" ? (
          <Group gap={6} wrap="nowrap">
            {DROFUS_INTERFACE_OCCURRENCE_FIELD_GROUPS.map((group) => (
              <Badge key={group} color={groupColorById[group] ?? "gray"} variant="light" radius="sm">
                {groupLabelById[group] ?? group}
              </Badge>
            ))}
          </Group>
        ) : (
          <Text size="sm" c="dimmed" style={{ whiteSpace: "nowrap" }}>
            Enkel visning av basisfelt
          </Text>
        )}
      </Group>

      <Box className="ag-theme-quartz" style={{ flex: 1, minHeight: 0, width: "100%" }}>
        <AgGridReact<DrofusForekomstRow>
          rowData={rowData}
          columnDefs={columnDefs}
          defaultColDef={defaultColDef}
          loading={isLoading}
          animateRows
          tooltipShowDelay={150}
          autoSizeStrategy={
            hasSavedCols ? undefined : { type: "fitGridWidth", defaultMinWidth: 120 }
          }
          groupHeaderHeight={36}
          headerHeight={60}
          rowGroupPanelShow="never"
          suppressScrollOnNewData
          sideBar={columnsOnlySideBar}
          suppressRowGroupHidesColumns
          suppressAggFuncInHeader
          pivotMode={false}
          components={{
            BooleanBadgeRenderer,
            DisciplineBadgeRenderer,
            ForekomstIdRenderer,
          }}
          {...gridStateHandlers}
          overlayNoRowsTemplate={
            occurrencesApiArg
              ? "<span style='padding:8px;display:inline-block;'>Ingen forekomster funnet</span>"
              : "<span style='padding:8px;display:inline-block;'>Ingen kjøring valgt</span>"
          }
        />
      </Box>

      <Group justify="space-between" align="center" wrap="nowrap">
        <Group gap="xs" wrap="nowrap">
          <Text size="sm" c="dimmed">
            Rader per side
          </Text>
          <Select
            size="xs"
            w={90}
            value={String(limit)}
            data={["50", "100", "250", "500"]}
            disabled={!occurrencesApiArg || isFetching}
            onChange={(value) => {
              const nextLimit = Number(value);
              if (Number.isFinite(nextLimit) && nextLimit > 0) {
                dispatch(setDrofusForekomsterLimit(nextLimit));
              }
            }}
          />
        </Group>
        <Pagination
          size="sm"
          total={totalPages}
          value={activePage}
          disabled={!occurrencesApiArg || isFetching}
          onChange={(page) => dispatch(setDrofusForekomsterOffset((page - 1) * limit))}
        />
      </Group>
    </Stack>
  );
};
